import { useState, useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { handleResponse } from '@/api/utils/ResponseHandler'
import { conceptKeys } from '@/api/queries/ConceptQueries'
import { vocabularyKeys } from '@/api/queries/VocabularyQueries'
import type { ConceptCategory, ConceptAssignment } from '@/db/Types'
import type { ConceptSide } from '@/types/Concept.types'

interface ConceptEditorState {
	name: string
	description: string
	category: ConceptCategory
	targetingMode: 'absolute_role' | 'relative_selector'
	ballPosition: 'left' | 'center' | 'right'
	playDirection: 'left' | 'right' | 'na'
	defaultSide: ConceptSide | null
	assignments: ConceptAssignment[]
	code: string
	isDirty: boolean
}

interface UseConceptEditorReturn {
	state: ConceptEditorState
	updateField: <K extends keyof ConceptEditorState>(field: K, value: ConceptEditorState[K]) => void
	addAssignment: (assignment: Omit<ConceptAssignment, 'id' | 'concept_id' | 'created_at'>) => void
	removeAssignment: (index: number) => void
	save: (playbookId: string, teamId: string | null) => Promise<string>
	isValid: boolean
	isSaving: boolean
	isLoadingConcept: boolean
}

async function fetchConcept(conceptId: string) {
	const response = await secureFetch(`/api/concepts/${conceptId}`)
	return handleResponse<{ concept: any }>(response)
}

async function saveConcept(
	url: string,
	method: string,
	body: Record<string, unknown>,
) {
	const response = await secureJsonFetch(url, { method, body })
	return handleResponse<{ concept: { id: string } }>(response)
}

async function createVocabularyForConcept(
	playbookId: string,
	code: string,
	category: ConceptCategory,
	conceptId: string,
	displayName: string,
) {
	await secureJsonFetch(`/api/playbooks/${playbookId}/vocabulary`, {
		method: 'POST',
		body: {
			code,
			category,
			entity_type: 'concept',
			entity_id: conceptId,
			display_name: displayName,
			auto_generated: false,
		},
	})
}

export function useConceptEditor(options?: { conceptId?: string; initialCategory?: ConceptCategory }): UseConceptEditorReturn {
	const queryClient = useQueryClient()

	const [state, setState] = useState<ConceptEditorState>({
		name: '',
		description: '',
		category: options?.initialCategory ?? 'route',
		targetingMode: 'absolute_role',
		ballPosition: 'center',
		playDirection: 'na',
		defaultSide: null,
		assignments: [],
		code: '',
		isDirty: false,
	})

	// Ref to always have current state available in mutation without stale closures
	const stateRef = useRef(state)
	stateRef.current = state

	// Load existing concept via React Query
	const { data: conceptData, isLoading: isLoadingConcept } = useQuery({
		queryKey: conceptKeys.baseConcept(options?.conceptId ?? ''),
		queryFn: () => fetchConcept(options!.conceptId!),
		enabled: !!options?.conceptId,
	})

	// Hydrate state from fetched concept — don't overwrite user edits
	useEffect(() => {
		if (!conceptData?.concept) return
		const c = conceptData.concept
		setState(prev => {
			if (prev.isDirty) return prev
			return {
				name: c.name ?? '',
				description: c.description ?? '',
				category: c.category ?? 'route',
				targetingMode: c.targeting_mode ?? 'absolute_role',
				ballPosition: c.ball_position ?? 'center',
				playDirection: c.play_direction ?? 'na',
				defaultSide: c.default_side ?? null,
				assignments: c.assignments ?? [],
				code: '',
				isDirty: false,
			}
		})
	}, [conceptData])

	const updateField = useCallback(<K extends keyof ConceptEditorState>(field: K, value: ConceptEditorState[K]) => {
		setState(prev => ({ ...prev, [field]: value, isDirty: true }))
	}, [])

	const addAssignment = useCallback((assignment: Omit<ConceptAssignment, 'id' | 'concept_id' | 'created_at'>) => {
		setState(prev => ({
			...prev,
			assignments: [...prev.assignments, { ...assignment, id: crypto.randomUUID(), concept_id: '', created_at: new Date() } as ConceptAssignment],
			isDirty: true,
		}))
	}, [])

	const removeAssignment = useCallback((index: number) => {
		setState(prev => ({
			...prev,
			assignments: prev.assignments.filter((_, i) => i !== index),
			isDirty: true,
		}))
	}, [])

	const saveMutation = useMutation({
		mutationFn: async ({ playbookId }: { playbookId: string; teamId: string | null }) => {
			// Read current state from ref to avoid stale closures
			const current = stateRef.current

			const method = options?.conceptId ? 'PUT' : 'POST'
			const url = options?.conceptId
				? `/api/concepts/${options.conceptId}`
				: `/api/playbooks/${playbookId}/concepts`

			const data = await saveConcept(url, method, {
				name: current.name,
				description: current.description,
				category: current.category,
				targeting_mode: current.targetingMode,
				ball_position: current.ballPosition,
				play_direction: current.playDirection,
				default_side: current.defaultSide,
				assignments: current.assignments.map(a => ({
					role: a.role,
					selector_type: a.selector_type,
					selector_params: a.selector_params,
					drawing_data: a.drawing_data,
					order_index: a.order_index,
				})),
			})

			const conceptId = data.concept?.id ?? options?.conceptId!

			// Auto-create vocabulary entry if new concept and code provided
			if (!options?.conceptId && current.code.trim()) {
				await createVocabularyForConcept(
					playbookId,
					current.code,
					current.category,
					conceptId,
					current.name,
				)
			}

			return conceptId
		},
		onSuccess: (_conceptId, { playbookId }) => {
			setState(prev => ({ ...prev, isDirty: false }))
			queryClient.invalidateQueries({ queryKey: conceptKeys.all })
			queryClient.invalidateQueries({ queryKey: vocabularyKeys.byPlaybook(playbookId) })
		},
	})

	const save = useCallback(async (playbookId: string, teamId: string | null): Promise<string> => {
		return saveMutation.mutateAsync({ playbookId, teamId })
	}, [saveMutation])

	const isValid = state.name.trim().length > 0

	return { state, updateField, addAssignment, removeAssignment, save, isValid, isSaving: saveMutation.isPending, isLoadingConcept }
}
